/**
 * FuelEU Maritime helpers: the shapes the API returns, the loaders that fetch
 * them, and the formatters the results screen renders them with.
 *
 * Deliberately thin. None of the regulation's arithmetic lives here — the
 * reduction ladder, the well-to-wake formula, the optimiser and the penalty are
 * all served by the API (`ecosail/fueleu.py`), and the form POSTs to
 * `fueleu_calculator/preview/` rather than recomputing them.
 *
 * That is the opposite of how the CII half of this app works, where
 * `calculateAttCII`/`calculateRefCII`/`calculateReqCII` in common.ts are a second
 * implementation of what models.py also does — and the two drifted, which is what
 * the vessel-label normalisation in common.ts and ecosail/tests.py exists to
 * clean up after. One implementation of a regulation is enough.
 */
import { globalAPI } from "./global";
import { queryKeys } from "./queryKeys";

/** One row of the Annex I/II factor table, as the fuel picker needs it. */
export interface FuelFactor {
  id: number;
  label: string;
  fuel_sub_type: number | null;
  fuel_sub_type_name: string | null;
  fuel_sub_type_short_name: string | null;
  fuel_class: "Fossil" | "Biofuel" | "RFNBO" | "Other" | null;
  engine_type: number | null;
  engine_type_name: string | null;
  engine_type_short_name: string | null;
  lcv: number | null;
  c_slip: number | null;
  co2_equivalent_factor_of_WtT: number | null;
  cfco2: number | null;
  cfch4: number | null;
  cfn2o: number | null;
  /** Well-to-wake gCO2eq/MJ. Null when the factor row cannot support one. */
  ghg_intensity: number | null;
  is_complete: boolean;
}

export interface FuelEUTarget {
  year: number;
  reduction: number;
  target_intensity: number;
}

export interface FuelEUTargets {
  reference_intensity: number;
  penalty_rate_eur: number;
  vlsfo_energy_per_tonne: number;
  rfnbo_reward_multiplier: number;
  rfnbo_reward_last_year: number;
  targets: FuelEUTarget[];
}

export interface FuelEUOptimisation {
  feasible: boolean;
  reason: string | null;
  /** Which fuels the answer is about - "your dirtiest fossil fuel" is not
   *  something the reader can check. Null when there is nothing to name. */
  displaced_fuel: string | null;
  /** Why that fuel and not another - blend half, gas oil, or dirtiest. */
  displaced_reason: string | null;
  alternative_fuel: string | null;
  /** How much of that fuel stops being burnt - the binding constraint. */
  displaced_share_of_its_own_fuel?: number | null;
  /** What fraction of the bunker the new fuel becomes. */
  alt_share_of_total_mass?: number | null;
  energy_share?: number;
  alt_energy?: number;
  alt_mass?: number;
  displaced_fossil_energy?: number;
  displaced_fossil_mass?: number;
  mass_share?: number;
  projected_energy?: number;
  projected_emissions?: number;
  projected_intensity?: number;
  projected_balance?: number;
}

export interface FuelEUSensitivityRow {
  energy_share: number;
  alt_energy: number;
  fossil_energy: number;
  /** The tonnages the share works out to. Null without an LCV to convert with. */
  alt_mass: number | null;
  displaced_fossil_mass: number | null;
  intensity: number;
  compliance_balance: number;
  status: "Surplus" | "Deficit" | "Break-even";
}

export interface FuelEUResultFuel {
  /** Which fuel this line is. Null only if the row lost its factor. */
  name: string | null;
  mass: number | null;
  lcv: number | null;
  intensity: number | null;
  energy: number;
  counted_energy: number;
  reward_multiplier: number;
  emissions: number;
  energy_share: number;
  emission_share: number;
}

export interface FuelEUResult {
  fuels: FuelEUResultFuel[];
  reference_intensity: number;
  reduction: number;
  target_intensity: number;
  total_energy: number;
  ops_energy: number;
  other_energy: number;
  total_emissions: number;
  /**
   * Eq.(1)'s answer: emissions over RWD-weighted fuel energy plus shore power.
   * The regulatory figure, compared against the target.
   */
  attained_intensity: number;
  /**
   * The same emissions over the raw fuel energy alone - no RWD, no OPS. "How
   * clean is the mix I bunkered", which is a different question and a different
   * number: equal to `attained_intensity` only when there is neither shore
   * power nor an RFNBO in the mix.
   */
  fuel_energy: number;
  fuel_intensity: number;
  fuel_wtt_intensity: number | null;
  fuel_ttw_intensity: number | null;
  /** The mix's two halves. Null once any fuel came in as a certified value. */
  wtt_intensity: number | null;
  ttw_intensity: number | null;
  /** Energy the mix contains but cannot account for - see MixIntensity. */
  unpriced_energy: number;
  /** Count, not energy: a fuel with no LCV has no MJ to report. */
  unpriced_fuels: number;
  has_unpriced_fuel: boolean;
  intensity_margin: number;
  compliance_balance: number;
  is_compliant: boolean;
  penalty_eur: number;
  borrowing_limit: number;
  optimisation: FuelEUOptimisation;
  sensitivity: FuelEUSensitivityRow[];
}

/* ------------------------------------------------------------- bio blend */

/**
 * The picker's "Bio blend" entry.
 *
 * Synthetic, and negative so it can never collide with a real factor row id. A
 * blend has no emission factors of its own — it *is* its components — so there
 * is deliberately no row for it in `tbl_fuel_multiplying_factor`, and the API
 * never sees this id. `buildPayload` expands the row into the two real fuels
 * before posting.
 */
export const BIO_BLEND_ID = -1;

/** Factor-table ids the blend is built from. */
export const BIO_DIESEL_FACTOR_ID = 19;
/** The fossil fuels a bio-diesel blend may be cut with. */
export const BLEND_FOSSIL_FACTOR_IDS = [1, 3];

export const bioBlendOption = (): FuelFactor =>
  ({
    id: BIO_BLEND_ID,
    label: "Bio blend (bio-diesel + fossil)",
    fuel_sub_type: null,
    fuel_sub_type_name: "Bio blend",
    fuel_sub_type_short_name: "BIOB",
    fuel_class: "Biofuel",
    engine_type: null,
    engine_type_name: null,
    engine_type_short_name: null,
    lcv: null,
    c_slip: null,
    co2_equivalent_factor_of_WtT: null,
    cfco2: null,
    cfch4: null,
    cfn2o: null,
    ghg_intensity: null,
    is_complete: false,
  } as FuelFactor);

export const isBioBlend = (row: FuelEURow | null | undefined): boolean =>
  !!row && row.fuel_factor === BIO_BLEND_ID;

/** One fuel row as the form holds it. Quantities are strings while being typed. */
export interface FuelEURow {
  fuel_factor: number | null;
  quantity: any;
  intensity_override: any;
  lcv_override: any;
  /** RED II Annex V lifecycle emissions, gCO2eq/MJ. Biofuels only. */
  e_value: any;
  /* --- bio blend only. Ignored unless fuel_factor is BIO_BLEND_ID. --- */
  /** Which fossil fuel the bio-diesel is cut with: HFO or Diesel Oil. */
  blend_fossil_factor: number | null;
  /** Split of the bunkered mass. The two must sum to 100. */
  blend_bio_percent: any;
  blend_fossil_percent: any;
}

export const emptyFuelRow = (): FuelEURow => ({
  fuel_factor: null,
  quantity: 0,
  intensity_override: "",
  lcv_override: "",
  e_value: "",
  blend_fossil_factor: null,
  blend_bio_percent: "",
  blend_fossil_percent: "",
});

/**
 * A blend row split into the two fuels it actually is.
 *
 * The percentages are **by mass**, which is how a bunkered blend is specified,
 * so the split is simply the tonnage apportioned — no energy weighting here.
 * That matters: doing it by mass and letting the API weight by energy is what
 * makes the result identical to entering the two fuels by hand, which is the
 * only definition of "correct" a blend has under Annex I. The regulation has no
 * concept of a blend; it has a list of fuels.
 *
 * The bio-diesel half carries the certified intensity and LCV, because Annex II
 * publishes neither for it.
 */
export const expandBioBlend = (row: FuelEURow) => {
  const total = Number(row.quantity) || 0;
  const bioPercent = numberOrNull(row.blend_bio_percent) ?? 0;
  const fossilPercent = numberOrNull(row.blend_fossil_percent) ?? 0;

  const parts = [
    {
      fuel_factor: BIO_DIESEL_FACTOR_ID,
      quantity: (total * bioPercent) / 100,
      // Bio-diesel is an Annex II biofuel: E and LCV, not an intensity.
      intensity_override: null,
      lcv_override: numberOrNull(row.lcv_override),
      e_value: numberOrNull(row.e_value),
      blend_role: "bio",
    },
    {
      fuel_factor: row.blend_fossil_factor,
      quantity: (total * fossilPercent) / 100,
      intensity_override: null,
      lcv_override: null,
      e_value: null,
      // The one thing the expansion would otherwise throw away. Two rows for
      // HFO and bio-diesel are indistinguishable from two loose fuels once the
      // percentages are tonnages — and the optimiser wants to know which fossil
      // fuel the operator is already blending into, because raising that blend
      // ratio is the change already available to them.
      blend_role: "fossil",
    },
  ];

  // A 100/0 blend is not a blend; dropping the empty half keeps it out of the
  // results breakdown as a zero-quantity line.
  return parts.filter((part) => part.fuel_factor && part.quantity > 0);
};

export const loadFuelFactors = async (): Promise<FuelFactor[]> => {
  try {
    const res = await globalAPI.get(queryKeys.FuelMultiplyingFactor.url());
    return res.data;
  } catch (err) {
    console.log(err);
    return [];
  }
};

export const loadFuelEUTargets = async (): Promise<FuelEUTargets | null> => {
  try {
    const res = await globalAPI.get(queryKeys.FuelEUTargets.url());
    return res.data;
  } catch (err) {
    console.log(err);
    return null;
  }
};

/**
 * Strip the form's empty strings before POSTing.
 *
 * The override boxes start as "" so they render blank rather than as a 0 the
 * user has to clear. DRF rejects "" for a nullable FloatField, and — worse — a 0
 * would be taken as a real certified intensity of zero, which is the best
 * possible value. Absent has to travel as null.
 */
export const numberOrNull = (value: any): number | null => {
  const text = String(value ?? "").trim();
  if (text === "") return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

/** The request body both `preview` and `create` take. */
export const buildPayload = (values: any) => ({
  name_of_vessel: values.name_of_vessel,
  imo_number: values.imo_number === "" ? null : values.imo_number,
  year: values.year,
  // Posted in kWh, the unit the shore connection is metered in. The API converts
  // to MJ (`fueleu.ops_energy_mj`) and stores megajoules — deliberately not done
  // here, so a client that forgets cannot silently understate the figure 3.6x.
  ops_energy_kwh: numberOrNull(values.ops_energy_kwh) ?? 0,
  // Still sent, still on the model: FuelEU counts other zero-intensity energy
  // toward the denominator too. The form stopped asking for it because OPS is
  // the only source anyone was entering, so it is pinned to 0 rather than
  // dropped from the API.
  other_energy: 0,
  alternative_fuel: values.alternative_fuel,
  alternative_intensity_override: numberOrNull(
    values.alternative_intensity_override
  ),
  alternative_lcv_override: numberOrNull(values.alternative_lcv_override),
  // Biofuel alternatives are described by E, like the bunkered ones. The API
  // derives the well-to-tank term (fueleu.wtt_from_e) so the optimiser prices a
  // candidate exactly as it would price that fuel once bunkered.
  alternative_e_value: numberOrNull(values.alternative_e_value),
  fuels: (values.fuels ?? [])
    // A row the user added and never filled in is not an input. Sending it would
    // make the mix look like it contains a zero-quantity fuel, which changes
    // nothing arithmetically but shows up as an empty line in the breakdown.
    .filter((row: FuelEURow) => row.fuel_factor && Number(row.quantity) > 0)
    // A blend becomes the two fuels it is made of. The API has no concept of a
    // blend and does not need one - Annex I works on a list of fuels, and two
    // rows is exactly what a blend is to it.
    .flatMap((row: FuelEURow) =>
      isBioBlend(row)
        ? expandBioBlend(row)
        : [
            {
              fuel_factor: row.fuel_factor,
              quantity: Number(row.quantity),
              intensity_override: numberOrNull(row.intensity_override),
              lcv_override: numberOrNull(row.lcv_override),
              e_value: numberOrNull(row.e_value),
              blend_role: null,
            },
          ]
    ),
});

/**
 * Compute an already-built payload without storing it.
 *
 * Separate from `previewFuelEU` so the form can watch the exact object it
 * posts - see `PreviewSync`.
 */
export const postPreview = async (payload: any): Promise<FuelEUResult> => {
  const res = await globalAPI.post(
    `${queryKeys.FuelEUCalculator.url()}preview/`,
    payload
  );
  return res.data;
};

/** Compute a mix without storing it. Used as the form's live preview. */
export const previewFuelEU = async (values: any): Promise<FuelEUResult> =>
  postPreview(buildPayload(values));

/** Store the calculation, so the lead form has a record to attach itself to. */
export const saveFuelEU = async (values: any) => {
  const res = await globalAPI.post(
    queryKeys.FuelEUCalculator.url(),
    buildPayload(values)
  );
  return res.data;
};

/* ------------------------------------------------------------ formatting */

/**
 * Compliance balances run to hundreds of millions of grams, which is unreadable
 * as a bare number and misleading rounded to "1.1e8". Shown as tonnes, the unit
 * the penalty is denominated in anyway.
 */
export const gramsToTonnes = (grams: number) => grams / 1e6;

export const formatNumber = (value: any, decimals = 2): string => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatPercent = (value: any, decimals = 2): string => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${formatNumber(number * 100, decimals)}%`;
};

/**
 * A compliance balance in tCO2eq, always signed.
 *
 * The sign is the entire message — a surplus and a deficit of the same size are
 * opposite news — and a bare "70,584.70" beside the word "Deficit" invites the
 * reader to take the label as decoration.
 */
export const formatBalance = (grams: any, decimals = 2): string => {
  const tonnes = gramsToTonnes(Number(grams));
  if (!Number.isFinite(tonnes)) return "—";
  const sign = tonnes > 0 ? "+" : tonnes < 0 ? "−" : "";
  return `${sign}${formatNumber(Math.abs(tonnes), decimals)}`;
};

export const formatEuro = (value: any): string => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `€${formatNumber(number, 0)}`;
};

/** MJ are large; GJ keeps the totals to four or five digits. */
export const formatEnergy = (mj: any, decimals = 1): string => {
  const number = Number(mj);
  if (!Number.isFinite(number)) return "—";
  return `${formatNumber(number / 1000, decimals)} GJ`;
};

/**
 * The CSS suffix for a compliance verdict, reusing the CII rating palette:
 * A (green) for a surplus, C (amber) for break-even, E (red) for a deficit.
 */
export const balanceRatingClass = (status: string): string => {
  if (status === "Surplus") return "A";
  if (status === "Break-even") return "C";
  return "E";
};

/**
 * Fuels grouped by class for the picker's option groups.
 *
 * Grouped rather than one flat list because the class is the first decision — an
 * operator looking for something to blend in is looking at Biofuel and RFNBO,
 * and the fossil rows are the thing being replaced. 'Other' sorts last; it is
 * the OTHER catch-all row and rarely what anyone wants.
 */
export const groupFuelFactors = (factors: FuelFactor[]) => {
  const order = ["Fossil", "Biofuel", "RFNBO", "Other"];
  return order
    .map((fuelClass) => ({
      label: fuelClass,
      options: factors.filter((factor) => factor.fuel_class === fuelClass),
    }))
    .filter((group) => group.options.length > 0);
};

/**
 * True when Annex II wants this fuel described by its E value rather than by a
 * well-to-wake intensity.
 *
 * The biofuel rows of Annex II publish no well-to-tank term and instead derive
 * one from the pathway:
 *
 *     WtT = E − C_fCO2 / LCV
 *
 * so E (RED II Annex V) and the LCV (RED II Annex III) are what the supplier's
 * proof of sustainability actually states, and what the form should ask for. The
 * API does the derivation — `fueleu.wtt_from_e`.
 *
 * Asking for a well-to-wake intensity instead and taking E as the answer
 * understates the fuel: the `C_fCO2/LCV` terms cancel against the tailpipe CO2,
 * leaving `E + (CFch4×25 + CFn2o×298)/LCV`, which for bio-diesel at LCV 0.037 is
 * 1.48 gCO₂eq/MJ above E.
 */
export const usesEValue = (factor: FuelFactor | undefined | null): boolean =>
  !!factor && factor.fuel_class === "Biofuel" && !factor.is_complete;

/**
 * True when this fuel cannot be used without the operator supplying values.
 *
 * Annex II publishes defaults for the fossil fuels and expects a certified
 * pathway value for most bio and e-fuels, so roughly a third of the seeded rows
 * arrive with a null LCV or well-to-tank term. That is the normal case for those
 * fuels rather than missing data, which is why they stay selectable and the form
 * asks for the numbers instead of hiding the option.
 */
export const needsOverride = (factor: FuelFactor | undefined | null): boolean =>
  !!factor && !factor.is_complete;
